/**
 * Persist draft CDU rows created before the parent request is saved (#996 Phase 2).
 * Mirrors smallUpsConfigurationDraft.
 */

import { api } from './api';
import { toCreateLiquidCoolingCduPayload } from './liquidCoolingCduPayload';
import { networkService } from './network';
import { offlineCache } from './offlineCache';

import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';

export async function persistDraftLiquidCoolingCduConfigurations(
  requestId: string | number,
  units: LiquidCoolingCduConfiguration[],
): Promise<void> {
  const createdIds: number[] = [];
  const idKey = String(requestId);

  try {
    for (const unit of units) {
      const created = await api.post<LiquidCoolingCduConfiguration>(
        `/requests/${requestId}/liquid-cooling-cdus`,
        toCreateLiquidCoolingCduPayload(unit),
      );
      if (created.id) {
        createdIds.push(created.id);
      }
    }
  } catch (error) {
    for (const liquidCoolingCduId of createdIds) {
      try {
        await api.delete(
          `/requests/${idKey}/liquid-cooling-cdus/${liquidCoolingCduId}`,
        );
      } catch (rollbackError) {
        console.error(
          'Failed to roll back CDU after partial draft flush:',
          rollbackError,
        );
      }
    }
    throw error;
  }
}

/**
 * Flush draft CDUs after Create Request save — online POST or offline cache + queue.
 */
export async function flushDraftLiquidCoolingCduConfigurations(
  requestId: string | number,
  units: LiquidCoolingCduConfiguration[],
  isOfflineRequest = false,
): Promise<void> {
  if (!units.length) {
    return;
  }

  const id = String(requestId);

  if (isOfflineRequest || !networkService.isConnected()) {
    const normalized = units.map((unit, index) => ({
      ...toCreateLiquidCoolingCduPayload(unit),
      id: unit.id && unit.id > 0 ? unit.id : -(Date.now() + index),
      requestId: typeof requestId === 'number' ? requestId : 0,
      displayOrder: unit.displayOrder ?? index + 1,
      status: unit.status ?? 'new',
    })) as LiquidCoolingCduConfiguration[];

    await offlineCache.updateCachedRequestLiquidCoolingCdus(id, normalized);
    for (const unit of normalized) {
      await offlineCache.addLiquidCoolingCduToOfflineQueue(
        id,
        'create',
        toCreateLiquidCoolingCduPayload(unit),
        unit.id,
      );
    }
    return;
  }

  await persistDraftLiquidCoolingCduConfigurations(requestId, units);
}
